<template>
    <LoadingView v-if="loading" />
    <article v-if="!loading" class="bg-white shadow sm:rounded">

        <header class="px-4 py-5 border-b border-oakwood-gray-200 sm:px-6 flex items-center justify-between gap-8 sticky top-0 bg-white">
            <h1 class="text-3xl leading-9 font-headline antialiased  font-medium text-bhav-blue-700">
                {{ id ? 'Edit Sales Team Member' : 'Add Sales Team Member'}}
            </h1>
            <nav class="flex flex-none gap-4 justify-end agents-center order-1 lg:order-2">
                <div v-if="agent.id" class="flex items-center gap-2">
                    <ToggleButton :id="`item-${agent.id}-status`" @changed="status()" :checked="agent.status == 'active'" 
                      :label="agent.status == 'active' ? 'Active' : 'Inactive'"/>
                      <label class="text-sm font-medium leading-5 text-oakwood-gray-700" :for="`item-${agent.id}-status`">{{ statuses.find(status => status.value == agent.status).label }}</label>
                </div>
                <div class="flex-none">
                    <FormButton 
                        color="gray"
                        @click="$router.go(-1)">
                        Cancel
                    </FormButton>
                </div>
                <div>
                    <FormButton @click="update" :disabled="!valid" color="blue">
                        Save
                    </FormButton>
                </div>
                <div v-if="agent.id">
                    <FormButton type="button" @click="openDelete()" prefix-class="fak" class="fa-trash" color="red"/>
                </div>
            </nav>
        </header>


        <form autocomplete="off" class="grid grid-cols-1 p-5 gap-5">
            <input autocomplete="false" name="hidden" type="text" style="display:none;">
            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <p class="block text-sm font-medium leading-5 text-bhav-blue-300 sm:mt-px sm:pt-2">
                    Photo
                </p>
                <UploadFiles v-model="photo_url" :multiple="false" class="col-span-3" />
            </div>
            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label for="first_name" class="block text-sm font-medium leading-6  text-bhav-blue-300">
                    First Name <span class="text-bhav-red cursor-help" title="This field is required">*</span>
                </label>
                <TextInput class="col-span-3" placeholder="First Name" id="first_name" v-model="agent.first_name"  />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label for="last_name" class="block text-sm font-medium leading-6  text-bhav-blue-300">
                    Last Name <span class="text-bhav-red cursor-help" title="This field is required">*</span>
                </label>
                <TextInput class="col-span-3" placeholder="Last Name" id="last_name" v-model="agent.last_name"/>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label for="email" class="block text-sm font-medium leading-6 text-bhav-blue-300">
                    Email
                </label>
                <TextInput class="col-span-3" placeholder="Email" id="email" v-model="agent.email" />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label for="phone" class="block text-sm font-medium leading-6 text-bhav-blue-300">
                    Phone <span class="text-bhav-red cursor-help" title="This field is required">*</span>
                </label>
                <PhoneNumber id="phone" v-model="agent.meta.phone" class="text-sm" />
            </div>
        </form>
        
    </article>

    <ModalDialog v-if="deleting" :open="deleting" @close="cancel">
        <template #header>
            <h2 class="text-lg text-bhav-blue-600 font-semibold">Confirm Sales Team Member Deletion</h2>
        </template>
        <template #body>
            <p class="text-bhav-blue-500"><strong>Attention!</strong> You are about to delete <em>{{ agent?.first_name }} {{ agent?.last_name }}</em>.</p>
            <p class="mt-2"><strong>This will detach this person from all collections and <em>permanently</em> delete their record.</strong></p>
            <p class="mt-2">Are you sure?</p>
        </template>
        <template #buttons>
            <FormButton type="button" @click="cancel" color="gray">
                Cancel
            </FormButton>
            <FormButton type="button" @click="purge" color="blue">
                Delete
            </FormButton>
        </template>
    </ModalDialog>
</template>

<script>
import axios from 'axios';
import LoadingView from '@/components/LoadingView.vue';
import { FormButton, UploadFiles, ToggleButton, PhoneNumber, TextInput } from '@/components/form';
import Agent from '@/models/agent';
import ModalDialog from '@/components/ModalDialog.vue';
import { agentStatuses, handle404 } from '@/helpers/globals';

export default {
    head() {
        return {
            title: () => `${this.$route.meta.title} %separator ${this.agent.first_name} ${this.agent.last_name}`
        }
    },
    data() {
        return {
            id: null,
            loading: true,
            agent: new Agent,
            deleting: false,
            statuses: agentStatuses
        }
    },
    methods: {
        show() {
            if(!this.id) return;
            axios.get(`agents/${this.id}`)
                .then(resp => {
                    this.agent = new Agent(resp.data);
                    this.loading = false;
                })
                .catch(err => {
                    if(err.response.status === 404)
                        handle404(this, err.response.data.message);
                })
        },
        update() {
            if(!this.id) {
                axios.postForm('agents', this.agent)
                    .then(() => {
                        this.$router.go(-1);
                    })
                    .catch(err => {
                        console.error(err);
                    })
            } else {
                axios.postForm(`agents/${this.id}?_method=PATCH`, this.agent)
                    .then(() => {
                        this.$router.go(-1);
                    })
                    .catch(err => {
                        console.error(err);
                    })
            }
        },
        openDelete() {
            this.deleting = true;
        },
        cancel() {
            this.deleting = false;
        },
        purge() {
            axios.delete(`agents/${this.agent.id}/purge`)
                .then(() => {
                    this.$router.go(-1);
                })
                .catch(err => {
                    console.error(err);
                })
        },
        status() {
            if(this.agent.status == 'active') {
                axios.delete(`agents/${this.agent.id}`)
                    .then(() => {
                        this.agent.status = 'inactive';
                    })
                    .catch(err => {
                        console.error(err);
                    })
            } else {
                axios.patch(`agents/${this.agent.id}/restore`)
                    .then(() => {
                        this.agent.status = 'active';
                    })
                    .catch(err => {
                        console.error(err);
                    })
            }
        },
        url(image) {
            return `${import.meta.env.VITE_APP_ENDPOINT}${image}`;
        }
    },
    computed: {
        photo_url: {
            get() {
                if(this.agent.photo_url) {
                    return [this.agent.photo_url]
                } else {
                    return this.agent.file == null ? [] : [this.agent.file]
                }
            },
            set(value) {
                this.agent.photo_url = null;
                this.agent.photo = null;
                this.agent.file = value[0];
            }
        },
        valid() {
            return this.agent.first_name && 
                this.agent.last_name && 
                this.agent.meta.phone;
        }
    },
    created() {
        if(this.$route.name == 'agent-edit') this.id = this.$route.params.id;
        if(this.id) {
            this.show();
        } else {
            this.loading = false;
        }
    },
    components: {
        LoadingView, FormButton, UploadFiles, ToggleButton, PhoneNumber, ModalDialog, TextInput
    }

}
</script>